import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Globe, Loader2, Settings, ShieldCheck } from "lucide-react";
import type {
  AdUnitType,
  BrandAdUnitAccess,
  BrandAdUnitApproval,
} from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

type Props = {
  adUnit: AdUnitType;
  currentBrandId: string;
};

type CuratableBrand = {
  id: string;
  name: string;
};

export function isBrandAdUnitCatalogQuery(query: { queryKey: readonly unknown[] }): boolean {
  const path = query.queryKey[0];
  return typeof path === "string" && /^\/api\/brands\/[^/]+\/ad-units$/.test(path);
}

export function invalidateAllBrandAdUnitCatalogs() {
  return queryClient.invalidateQueries({ predicate: isBrandAdUnitCatalogQuery });
}

export function invalidateBrandAdUnitCatalog(brandId: string) {
  return queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
}

export function AdUnitBrandCuration({ adUnit, currentBrandId }: Props) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryBase = `/api/admin/ad-units/${adUnit.id}`;

  const { data: brands = [], isLoading: brandsLoading } = useQuery<CuratableBrand[]>({
    queryKey: [`${queryBase}/brands`],
  });
  const { data: assignments = [], isLoading: assignmentsLoading } = useQuery<BrandAdUnitAccess[]>({
    queryKey: [`${queryBase}/assignments`],
  });
  const { data: approvals = [], isLoading: approvalsLoading } = useQuery<BrandAdUnitApproval[]>({
    queryKey: [`${queryBase}/approvals`],
  });

  const assignedBrandIds = useMemo(
    () => new Set(assignments.map((assignment) => assignment.brandId)),
    [assignments],
  );
  const approvedBrandIds = useMemo(
    () => new Set(approvals.map((approval) => approval.brandId)),
    [approvals],
  );
  const isBrandOwned = !!adUnit.brandId;
  const isRestricted = assignments.length > 0;
  const currentBrand = brands.find((brand) => brand.id === currentBrandId);
  const currentBrandHasAccess = isBrandOwned
    ? adUnit.brandId === currentBrandId
    : !isRestricted || assignedBrandIds.has(currentBrandId);
  const currentBrandIsApproved =
    currentBrandHasAccess && approvedBrandIds.has(currentBrandId);
  const isLoading = brandsLoading || assignmentsLoading || approvalsLoading;

  const refreshAssignments = () => {
    queryClient.invalidateQueries({ queryKey: [`${queryBase}/assignments`] });
    // One explicit assignment changes this template from unrestricted to
    // restricted, while removing the last one opens it to every brand again.
    // Either transition can change every brand's resolved catalog.
    invalidateAllBrandAdUnitCatalogs();
  };
  const refreshApprovals = (brandId: string) => {
    queryClient.invalidateQueries({ queryKey: [`${queryBase}/approvals`] });
    invalidateBrandAdUnitCatalog(brandId);
  };

  const accessMutation = useMutation({
    mutationFn: async ({
      brandId,
      explicitlyGranted,
    }: {
      brandId: string;
      explicitlyGranted: boolean;
    }) => {
      await apiRequest(
        explicitlyGranted ? "POST" : "DELETE",
        `/api/admin/ad-units/${adUnit.id}/brands/${brandId}`,
      );
    },
    onSuccess: refreshAssignments,
    onError: (error: Error) =>
      toast({
        title: "Could not update template access",
        description: error.message,
        variant: "destructive",
      }),
  });

  const approvalMutation = useMutation({
    mutationFn: async ({
      brandId,
      approved,
    }: {
      brandId: string;
      approved: boolean;
    }) => {
      await apiRequest(
        approved ? "POST" : "DELETE",
        `/api/admin/ad-units/${adUnit.id}/brands/${brandId}/approval`,
      );
    },
    onSuccess: (_row, variables) => refreshApprovals(variables.brandId),
    onError: (error: Error) =>
      toast({
        title: "Could not update template approval",
        description: error.message,
        variant: "destructive",
      }),
  });

  const makeUnrestrictedMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/admin/ad-units/${adUnit.id}/assignments`);
    },
    onSuccess: () => {
      refreshAssignments();
      toast({ title: "Template is now accessible to all brands" });
    },
    onError: (error: Error) =>
      toast({
        title: "Could not update template access",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <>
      <Card className="border-primary/20 bg-primary/[0.025]" data-testid="card-brand-ad-unit-curation">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold">Brand access &amp; approvals</h2>
                  <Badge variant="outline" className="text-[10px]">
                    {isBrandOwned ? "Brand template" : "Global template"}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isBrandOwned
                    ? "Control whether this brand-owned template is shown by default in Ads Express."
                    : "Control whether this template is accessible and shown by default in each brand's Ads Express library."}
                </p>
                {!isLoading && (
                  <p className="mt-2 text-xs" data-testid="text-current-brand-template-status">
                    <span className="font-medium">
                      {currentBrand?.name ?? "Current brand"}:
                    </span>{" "}
                    <span className={currentBrandHasAccess ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>
                      {currentBrandHasAccess ? "Accessible" : "Restricted"}
                    </span>
                    {" · "}
                    <span className={currentBrandIsApproved ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}>
                      {currentBrandIsApproved ? "Default in Ads Express" : "Not default in Ads Express"}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {isLoading ? (
              <Skeleton className="h-9 w-64" />
            ) : (
              <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor={`current-brand-approval-${adUnit.id}`}
                    className="text-xs font-medium"
                  >
                    Default in Ads Express
                  </Label>
                  <Switch
                    id={`current-brand-approval-${adUnit.id}`}
                    checked={currentBrandIsApproved}
                    onCheckedChange={(approved) =>
                      approvalMutation.mutate({ brandId: currentBrandId, approved })
                    }
                    disabled={!currentBrandHasAccess || approvalMutation.isPending}
                    data-testid="switch-current-brand-ad-unit-approval"
                  />
                </div>
                {!isBrandOwned && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDialogOpen(true)}
                    data-testid="button-manage-brand-ad-unit-curation"
                  >
                    <Settings className="mr-1.5 h-3.5 w-3.5" />
                    Manage all brands
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!isBrandOwned && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Brand access &amp; approvals</DialogTitle>
            <DialogDescription>
              Access determines whether a brand can use “{adUnit.name}.” Approval only controls whether an accessible template is shown by default in Ads Express.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-3">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="h-4 w-4 text-muted-foreground" />
              {isRestricted
                ? `Restricted to ${assignments.length} explicitly selected brand${assignments.length === 1 ? "" : "s"}`
                : "Accessible to all brands"}
            </div>
            {isRestricted && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => makeUnrestrictedMutation.mutate()}
                disabled={makeUnrestrictedMutation.isPending}
                data-testid="button-make-ad-unit-unrestricted"
              >
                {makeUnrestrictedMutation.isPending && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Make accessible to all
              </Button>
            )}
          </div>

          <div className="grid grid-cols-[120px_1fr_auto] gap-3 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Explicit access</span>
            <span>Brand</span>
            <span>Default in Ads Express</span>
          </div>
          <div className="max-h-[420px] space-y-2 overflow-y-auto">
            {brands.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No brands available</p>
            ) : (
              brands.map((brand) => {
                const explicitlyGranted = assignedBrandIds.has(brand.id);
                const hasAccess = !isRestricted || explicitlyGranted;
                const approved = hasAccess && approvedBrandIds.has(brand.id);
                const isCurrent = brand.id === currentBrandId;
                return (
                  <div
                    key={brand.id}
                    className={`flex items-center gap-3 rounded-md p-2 hover:bg-muted ${isCurrent ? "bg-primary/5 ring-1 ring-primary/15" : ""}`}
                    data-testid={`row-brand-ad-unit-curation-${brand.id}`}
                  >
                    <div className="flex w-[108px] shrink-0 items-center gap-2">
                      <Checkbox
                        id={`detail-access-${adUnit.id}-${brand.id}`}
                        checked={explicitlyGranted}
                        onCheckedChange={(checked) =>
                          accessMutation.mutate({
                            brandId: brand.id,
                            explicitlyGranted: checked === true,
                          })
                        }
                        disabled={accessMutation.isPending}
                        data-testid={`checkbox-detail-ad-unit-access-${brand.id}`}
                      />
                      <Label
                        htmlFor={`detail-access-${adUnit.id}-${brand.id}`}
                        className="text-xs text-muted-foreground"
                      >
                        Grant
                      </Label>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{brand.name}</span>
                        {isCurrent && (
                          <Badge variant="secondary" className="text-[9px]">
                            Current
                          </Badge>
                        )}
                      </div>
                      <p className={`text-[10px] ${hasAccess ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        {hasAccess
                          ? isRestricted
                            ? "Can access this template"
                            : "Can access via the all-brands default"
                          : "Restricted from this template"}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {approved && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                      <Switch
                        aria-label={`Show ${adUnit.name} by default for ${brand.name}`}
                        checked={approved}
                        onCheckedChange={(nextApproved) =>
                          approvalMutation.mutate({
                            brandId: brand.id,
                            approved: nextApproved,
                          })
                        }
                        disabled={!hasAccess || approvalMutation.isPending}
                        data-testid={`switch-detail-ad-unit-approval-${brand.id}`}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {!isRestricted && (
            <p className="text-xs text-muted-foreground">
              Selecting the first explicit access checkbox changes this template from all-brands access to a restricted brand list.
            </p>
          )}
        </DialogContent>
      </Dialog>}
    </>
  );
}